// Singleton WebSocket service for the Realtime server (/ws).
// - connects with the Firebase ID token as ?token=
// - intercepts control frames; dispatches data frames to registered handlers
// - tracks subscriptions and replays them after (re)connect
// - 30s keep-alive ping; unbounded exponential-backoff reconnect (capped at 30s)
//   + instant revival on tab focus / visibility / network-online (laptop sleep, VPN flap)
// - command(action,symbol,params): Promise correlated by action+symbol (server makes the id)
import { config } from './config';
import { getIdToken } from './firebase';
import { perf } from './perf';

type Handler = (msg: any) => void;
// Optional dispatch filter: which frame types (and, if per-symbol, which symbol)
// a handler wants. Frames are routed only to matching handlers instead of being
// offered to every registered handler (2×watchlist+ closures per frame before).
// The filter must be a SUPERSET of the handler's own predicate — handlers keep
// their fine-grained checks (e.g. `!m.data`), so routing is purely an optimization.
export type MsgFilter = { types: string[]; symbol?: string };
type Pending = { resolve: (v: any) => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> };

const MAX_BACKOFF_MS = 30000;
const COMMAND_TIMEOUT_MS = 5000;
const KEEPALIVE_MS = 30000;

class WsService {
  private ws: WebSocket | null = null;
  private handlers = new Set<Handler>(); // unfiltered handlers — see every data frame
  // type → (symbol | '*') → handlers. Routed in dispatch(); '*' = any symbol.
  private byType = new Map<string, Map<string, Set<Handler>>>();
  // channel → subscriber ref-count. Multiple consumers (chart + watchlist) can want
  // the same channel; only send subscribe on 0→1 and unsubscribe on 1→0 so one
  // consumer's teardown doesn't kill another's live subscription.
  private channels = new Map<string, number>();
  private pending = new Map<string, Pending>();
  private keepAlive: ReturnType<typeof setInterval> | null = null;
  private reconnectAttempts = 0;
  private intentionalClose = false;
  private connected = false;
  private sessionId = `session_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  constructor() {
    // Revive the socket when the machine/tab comes back. Without these, any outage
    // that outlives the backoff window (laptop sleep, VPN flap, network blip) left
    // the tab silently rendering its last snapshot forever.
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.ensureAlive());
      window.addEventListener('focus', () => this.ensureAlive());
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.ensureAlive();
      });
    }
  }

  /** Reconnect immediately if the socket is not live (wake/online/focus events). */
  private ensureAlive() {
    if (this.intentionalClose) return;
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    this.reconnectAttempts = 0; // wake = fresh start, no stale 30s backoff
    this.connect().catch(() => this.scheduleReconnect());
  }

  /** Register a handler for data frames (trade/order/quote/indicators/...). Returns an unsubscribe fn.
   *  Pass a filter so the handler is only invoked for matching frames; without one
   *  the handler sees every data frame (avoid for hot paths). */
  onMessage(h: Handler, filter?: MsgFilter): () => void {
    if (!filter || !filter.types || !filter.types.length) {
      this.handlers.add(h);
      return () => this.handlers.delete(h);
    }
    const symKey = filter.symbol == null ? '*' : filter.symbol;
    for (const t of filter.types) {
      let bySym = this.byType.get(t);
      if (!bySym) { bySym = new Map(); this.byType.set(t, bySym); }
      let set = bySym.get(symKey);
      if (!set) { set = new Set(); bySym.set(symKey, set); }
      set.add(h);
    }
    return () => {
      for (const t of filter.types) {
        const bySym = this.byType.get(t);
        const set = bySym && bySym.get(symKey);
        if (!set) continue;
        set.delete(h);
        if (!set.size) bySym!.delete(symKey);
        if (bySym && !bySym.size) this.byType.delete(t);
      }
    };
  }

  isConnected() {
    return this.connected;
  }

  async connect(): Promise<void> {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    this.intentionalClose = false;
    let token: string | null | undefined;
    try {
      token = await getIdToken();
    } catch {
      // Transient auth failure (e.g. token refresh right after wake): no socket exists
      // yet so no onclose will fire — schedule the retry ourselves instead of dying.
      this.scheduleReconnect();
      return;
    }
    const url = token ? `${config.wsUrl}?token=${encodeURIComponent(token)}` : config.wsUrl;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.connected = true;
      this.reconnectAttempts = 0;
      this.startKeepAlive();
      this.resubscribe();
    };
    ws.onmessage = (e) => {
      // perf probe: time parse + the full synchronous dispatch, per frame type.
      // A single boolean check when the instrument is off.
      const t0 = perf.on ? performance.now() : 0;
      let msg: any;
      try { msg = JSON.parse(e.data); } catch { return; }
      this.handle(msg);
      if (perf.on) perf.wsFrame(msg.type || '?', typeof e.data === 'string' ? e.data.length : 0, performance.now() - t0);
    };
    ws.onclose = () => {
      this.connected = false;
      this.stopKeepAlive();
      if (!this.intentionalClose) this.scheduleReconnect();
    };
    ws.onerror = () => { /* close handler does the cleanup */ };
  }

  disconnect() {
    this.intentionalClose = true;
    this.stopKeepAlive();
    try { this.ws?.close(); } catch { /* ignore */ }
    this.ws = null;
    this.connected = false;
  }

  subscribe(channel: string) {
    const n = (this.channels.get(channel) || 0) + 1;
    this.channels.set(channel, n);
    if (n === 1) this.send({ type: 'subscribe', channel });
  }

  unsubscribe(channel: string) {
    const n = (this.channels.get(channel) || 0) - 1;
    if (n <= 0) {
      this.channels.delete(channel);
      this.send({ type: 'unsubscribe', channel });
    } else {
      this.channels.set(channel, n);
    }
  }

  /** Wait until the socket is OPEN (reconnecting if needed), up to `timeoutMs`. */
  private async waitForOpen(timeoutMs = 8000): Promise<boolean> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return true;
    if (!this.ws || this.ws.readyState === WebSocket.CLOSED) { try { await this.connect(); } catch { /* ignore */ } }
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) return true;
      await new Promise((r) => setTimeout(r, 80));
    }
    return this.ws != null && this.ws.readyState === WebSocket.OPEN;
  }

  /** Send a trading command; resolves on command_ack, rejects on command_error/timeout. */
  command(action: string, symbol: string, params: Record<string, unknown> = {}): Promise<any> {
    // Normalize the symbol — the server uses it verbatim for the stream key
    // (stream:manual_commands:<SYM>), so case/whitespace must match the worker.
    const sym = String(symbol || '').trim().toUpperCase();
    const key = `${action}_${sym}`;
    if (this.pending.has(key)) return Promise.reject(new Error('Command already in progress'));

    // Reserve the key synchronously so a second fast click can't slip past the dedup
    // during the async connection wait below.
    let entry!: Pending;
    const promise = new Promise<any>((resolve, reject) => { entry = { resolve, reject, timer: null as any }; });
    this.pending.set(key, entry);

    // Don't drop the command on a momentarily-closed socket — wait for it (like Canvas),
    // then arm the ack timeout only once we've actually sent.
    this.waitForOpen().then((open) => {
      if (!this.pending.get(key)) return; // already settled/cleared
      if (!open) {
        this.pending.delete(key);
        entry.reject(new Error('Not connected to realtime server'));
        return;
      }
      entry.timer = setTimeout(() => {
        this.pending.delete(key);
        entry.reject(new Error(`Command timed out: ${action} ${sym}`));
      }, COMMAND_TIMEOUT_MS);
      this.send({ type: 'command', action, symbol: sym, sessionId: this.sessionId, params });
    }).catch((e) => {
      this.pending.delete(key);
      entry.reject(e instanceof Error ? e : new Error(String(e)));
    });

    return promise;
  }

  // ---- internals ----
  private handle(msg: any) {
    switch (msg.type) {
      case 'command_ack': return this.settle(msg, true);
      case 'command_error': return this.settle(msg, false);
      // control frames — swallow (resubscription is driven by onopen)
      case 'authenticated':
      case 'auth_error':
      case 'subscribed':
      case 'unsubscribed':
      case 'pong':
      case 'connected':
        return;
      default:
        this.dispatch(msg);
    }
  }

  /** Route a data frame to unfiltered handlers + the matching (type, symbol) buckets. */
  private dispatch(msg: any) {
    this.handlers.forEach((h) => h(msg));
    const bySym = this.byType.get(msg.type);
    if (!bySym) return;
    const star = bySym.get('*');
    if (star) star.forEach((h) => h(msg));
    if (msg.symbol != null) {
      const forSym = bySym.get(String(msg.symbol));
      if (forSym) forSym.forEach((h) => h(msg));
    }
  }

  private settle(msg: any, ok: boolean) {
    const key = `${msg.action}_${msg.symbol}`;
    const p = this.pending.get(key);
    if (!p) return;
    clearTimeout(p.timer);
    this.pending.delete(key);
    if (ok) p.resolve(msg);
    else p.reject(new Error(msg.error || 'command_error'));
  }

  private send(o: unknown) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(o));
  }

  private resubscribe() {
    this.channels.forEach((_count, channel) => this.send({ type: 'subscribe', channel }));
  }

  private startKeepAlive() {
    this.stopKeepAlive();
    this.keepAlive = setInterval(() => this.send({ type: 'ping' }), KEEPALIVE_MS);
  }

  private stopKeepAlive() {
    if (this.keepAlive) clearInterval(this.keepAlive);
    this.keepAlive = null;
  }

  private scheduleReconnect() {
    // Never give up: a live trading UI must always claw its way back. Backoff is
    // capped at 30s, and ensureAlive() resets it to instant on wake/online/focus.
    const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** this.reconnectAttempts);
    this.reconnectAttempts++;
    setTimeout(() => {
      if (this.intentionalClose) return;
      // connect() can throw before a socket exists (token fetch) — reschedule on failure
      this.connect().catch(() => this.scheduleReconnect());
    }, delay);
  }
}

export const ws = new WsService();
